import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { MasterCartProvider } from "@/contexts/MasterCartContext";
import { MasterCartUI } from "@/components/cart/MasterCart";
import { AppLayout } from "@/components/AppLayout";
import { ProtectedRoute, OnboardingRoute, AdminRoute, DataCapturerRoute } from "@/components/ProtectedRoute";
import { SellerRoute } from "@/components/SellerRoute";
import { FoodpreneurApprovalGate } from "@/components/FoodpreneurApprovalGate";
import { AdminLayout } from "@/components/admin/AdminLayout";

// Eager: landing page (LCP) + lightweight auth entry pages users hit immediately
import LandingPage from "./pages/LandingPage";
import NotFound from "./pages/NotFound";

// Lazy: all other routes — keeps the initial JS bundle small
// Pricing page hidden
// const PricingPage = lazy(() => import("./pages/PricingPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const GoodbyePage = lazy(() => import("./pages/GoodbyePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const TradeInterestPage = lazy(() => import("./pages/brand/TradeInterestPage"));
const DealCardsPage = lazy(() => import("./pages/brand/DealCardsPage"));
const RFQPage = lazy(() => import("./pages/spaza/RFQPage"));
const BidDashboardPage = lazy(() => import("./pages/spaza/BidDashboardPage"));
const CollaborativeBidsPage = lazy(() => import("./pages/spaza/CollaborativeBidsPage"));
const PromotionsPage = lazy(() => import("./pages/community/PromotionsPage"));
const DemandMapPage = lazy(() => import("./pages/community/DemandMapPage"));
const DeliveryManagementPage = lazy(() => import("./pages/logistics/DeliveryManagementPage"));
const MarketIntelligencePage = lazy(() => import("./pages/MarketIntelligencePage"));
const ProductIndexPage = lazy(() => import("./pages/spaza/ProductIndexPage"));
const OrdersPage = lazy(() => import("./pages/spaza/OrdersPage"));
const ProductRequestsPage = lazy(() => import("./pages/data-capturer/ProductRequestsPage"));
const ScrapeManagementPage = lazy(() => import("./pages/data-capturer/ScrapeManagementPage"));
const StoreManagementPage = lazy(() => import("./pages/data-capturer/StoreManagementPage"));
const CategoryManagementPage = lazy(() => import("./pages/data-capturer/CategoryManagementPage"));
const ProductManagementPage = lazy(() => import("./pages/data-capturer/ProductManagementPage"));
const CheckoutConfirmPage = lazy(() => import("./pages/CheckoutConfirmPage"));
const CheckoutPaymentPage = lazy(() => import("./pages/CheckoutPaymentPage"));
const CheckoutGatewayPage = lazy(() => import("./pages/CheckoutGatewayPage"));
const CheckoutProcessingPage = lazy(() => import("./pages/CheckoutProcessingPage"));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage"));
const OrderTrackingPage = lazy(() => import("./pages/OrderTrackingPage"));
const OrdersListPage = lazy(() => import("./pages/OrdersListPage"));
const AdminOrderDetailPage = lazy(() => import("./pages/admin/AdminOrderDetailPage"));
const AdminOverviewPage = lazy(() => import("./pages/admin/AdminOverviewPage"));
const AdminScrapesPage = lazy(() => import("./pages/admin/AdminScrapesPage"));
const AdminCataloguePage = lazy(() => import("./pages/admin/AdminCataloguePage"));
const AdminProductsPage = lazy(() => import("./pages/admin/AdminProductsPage"));
const AdminOrdersPage = lazy(() => import("./pages/admin/AdminOrdersPage"));
const AdminUsersPage = lazy(() => import("./pages/admin/AdminUsersPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const WhatsAppSettingsPage = lazy(() => import("./pages/settings/WhatsAppSettingsPage"));
const MyLocationPage = lazy(() => import("./pages/settings/MyLocationPage"));
const ProfileSettingsPage = lazy(() => import("./pages/settings/ProfilePage"));
const BasketPreferencesPage = lazy(() => import("./pages/settings/BasketPreferencesPage"));
const MarketplacePage = lazy(() => import("./pages/marketplace/MarketplacePage"));
const MarketplaceProductPage = lazy(() => import("./pages/marketplace/MarketplaceProductPage"));
const SellerPublicPage = lazy(() => import("./pages/marketplace/SellerPublicPage"));
const SellerApplyPage = lazy(() => import("./pages/seller/SellerApplyPage"));
const SellerDashboardPage = lazy(() => import("./pages/seller/SellerDashboardPage"));
const SellerProductsPage = lazy(() => import("./pages/seller/SellerProductsPage"));
const SellerStorefrontPage = lazy(() => import("./pages/seller/SellerStorefrontPage"));
const SellerOrdersPage = lazy(() => import("./pages/seller/SellerOrdersPage"));
const MyOrdersPage = lazy(() => import("./pages/marketplace/MyOrdersPage"));
const MyOrderDetailPage = lazy(() => import("./pages/marketplace/MyOrderDetailPage"));
const AdminSellersPage = lazy(() => import("./pages/admin/AdminSellersPage"));
const SupplierApplyIntroPage = lazy(() => import("./pages/suppliers/SupplierApplyIntroPage"));
const SupplierApplyPage = lazy(() => import("./pages/suppliers/SupplierApplyPage"));
const SupplierApplySuccessPage = lazy(() => import("./pages/suppliers/SupplierApplySuccessPage"));
const AdminSupplierApplicationsPage = lazy(() => import("./pages/admin/AdminSupplierApplicationsPage"));
const AdminSupplierApplicationDetailPage = lazy(() => import("./pages/admin/AdminSupplierApplicationDetailPage"));
const AdminFoodpreneurApprovalsPage = lazy(() => import("./pages/admin/AdminFoodpreneurApprovalsPage"));
const AdminApprovalsHubPage = lazy(() => import("./pages/admin/AdminApprovalsHubPage"));
const PendingApprovalPage = lazy(() => import("./pages/PendingApprovalPage"));

// Internal staff + supplier portal
const RepDashboardPage = lazy(() => import("./pages/staff/RepDashboardPage"));
const CapturerDashboardPage = lazy(() => import("./pages/staff/CapturerDashboardPage"));
const ManagerDashboardPage = lazy(() => import("./pages/staff/ManagerDashboardPage"));
const ManagerUsersPage = lazy(() => import("./pages/staff/ManagerUsersPage"));
const ManagerCreateSupplierPage = lazy(() => import("./pages/staff/ManagerCreateSupplierPage"));
const ManagerSupplierChangesPage = lazy(() => import("./pages/staff/ManagerSupplierChangesPage"));
const SupplierHomePage = lazy(() => import("./pages/supplier/SupplierHomePage"));
const SupplierProfileEditPage = lazy(() => import("./pages/supplier/SupplierProfileEditPage"));
const SupplierProductNewPage = lazy(() => import("./pages/supplier/SupplierProductNewPage"));
const SupplierPriceEditPage = lazy(() => import("./pages/supplier/SupplierPriceEditPage"));
import { StaffRoute } from "@/components/StaffRoute";

function ProtectedSellerLayout({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AppLayout>
        <SellerRoute>{children}</SellerRoute>
      </AppLayout>
    </ProtectedRoute>
  );
}

const queryClient = new QueryClient();

function ProtectedAppLayout({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <FoodpreneurApprovalGate>
        <AppLayout>{children}</AppLayout>
      </FoodpreneurApprovalGate>
    </ProtectedRoute>
  );
}

function ProtectedAdminLayout({ children }: { children: React.ReactNode }) {
  return (
    <AdminRoute>
      <AdminLayout>{children}</AdminLayout>
    </AdminRoute>
  );
}

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background" />
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <CartProvider>
          <MasterCartProvider>
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              {/* <Route path="/pricing" element={<PricingPage />} /> */}
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/goodbye" element={<GoodbyePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/suppliers/apply" element={<SupplierApplyIntroPage />} />
              <Route path="/suppliers/apply/form" element={<SupplierApplyPage />} />
              <Route path="/suppliers/apply/success" element={<SupplierApplySuccessPage />} />
              <Route path="/onboarding" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />
              <Route path="/account/pending-approval" element={<ProtectedRoute><PendingApprovalPage /></ProtectedRoute>} />


              {/* Protected routes */}
              <Route path="/dashboard" element={<ProtectedAppLayout><DashboardPage /></ProtectedAppLayout>} />
              <Route path="/brand/trade" element={<ProtectedAppLayout><TradeInterestPage /></ProtectedAppLayout>} />
              <Route path="/brand/deals" element={<ProtectedAppLayout><DealCardsPage /></ProtectedAppLayout>} />
              <Route path="/spaza/rfq" element={<ProtectedAppLayout><RFQPage /></ProtectedAppLayout>} />
              <Route path="/spaza/bids" element={<ProtectedAppLayout><BidDashboardPage /></ProtectedAppLayout>} />
              <Route path="/spaza/collab-bids" element={<ProtectedAppLayout><CollaborativeBidsPage /></ProtectedAppLayout>} />
              <Route path="/spaza/products" element={<ProtectedAppLayout><ProductIndexPage /></ProtectedAppLayout>} />
              <Route path="/spaza/orders" element={<ProtectedAppLayout><OrdersPage /></ProtectedAppLayout>} />
              <Route path="/community/promotions" element={<ProtectedAppLayout><PromotionsPage /></ProtectedAppLayout>} />
              <Route path="/community/demand" element={<ProtectedAppLayout><DemandMapPage /></ProtectedAppLayout>} />
              <Route path="/logistics/deliveries" element={<ProtectedAppLayout><DeliveryManagementPage /></ProtectedAppLayout>} />
              <Route path="/intelligence" element={<ProtectedAppLayout><MarketIntelligencePage /></ProtectedAppLayout>} />
              <Route path="/data-capturer/requests" element={<DataCapturerRoute><ProtectedAppLayout><ProductRequestsPage /></ProtectedAppLayout></DataCapturerRoute>} />
              <Route path="/data-capturer/scrapes" element={<DataCapturerRoute><ProtectedAppLayout><ScrapeManagementPage /></ProtectedAppLayout></DataCapturerRoute>} />
              <Route path="/data-capturer/stores" element={<DataCapturerRoute><ProtectedAppLayout><StoreManagementPage /></ProtectedAppLayout></DataCapturerRoute>} />
              <Route path="/data-capturer/categories" element={<DataCapturerRoute><ProtectedAppLayout><CategoryManagementPage /></ProtectedAppLayout></DataCapturerRoute>} />
              <Route path="/data-capturer/products" element={<DataCapturerRoute><ProtectedAppLayout><ProductManagementPage /></ProtectedAppLayout></DataCapturerRoute>} />

              <Route path="/checkout/confirm" element={<ProtectedAppLayout><CheckoutConfirmPage /></ProtectedAppLayout>} />
              <Route path="/checkout/payment" element={<ProtectedAppLayout><CheckoutPaymentPage /></ProtectedAppLayout>} />
              <Route path="/checkout/gateway" element={<ProtectedRoute><CheckoutGatewayPage /></ProtectedRoute>} />
              <Route path="/checkout/processing" element={<ProtectedRoute><CheckoutProcessingPage /></ProtectedRoute>} />
              <Route path="/checkout/success" element={<ProtectedAppLayout><CheckoutSuccessPage /></ProtectedAppLayout>} />
              <Route path="/orders" element={<ProtectedAppLayout><OrdersListPage /></ProtectedAppLayout>} />
              <Route path="/orders/:id" element={<ProtectedAppLayout><OrderTrackingPage /></ProtectedAppLayout>} />
              <Route path="/notifications" element={<ProtectedAppLayout><NotificationsPage /></ProtectedAppLayout>} />
              <Route path="/settings" element={<ProtectedAppLayout><WhatsAppSettingsPage /></ProtectedAppLayout>} />
              <Route path="/settings/whatsapp" element={<ProtectedAppLayout><WhatsAppSettingsPage /></ProtectedAppLayout>} />
              <Route path="/settings/location" element={<ProtectedAppLayout><MyLocationPage /></ProtectedAppLayout>} />
              <Route path="/settings/profile" element={<ProtectedAppLayout><ProfileSettingsPage /></ProtectedAppLayout>} />
              <Route path="/settings/basket" element={<ProtectedAppLayout><BasketPreferencesPage /></ProtectedAppLayout>} />

              {/* Marketplace (buyer) */}
              <Route path="/marketplace" element={<ProtectedAppLayout><MarketplacePage /></ProtectedAppLayout>} />
              <Route path="/marketplace/product/:id" element={<ProtectedAppLayout><MarketplaceProductPage /></ProtectedAppLayout>} />
              <Route path="/marketplace/seller/:slug" element={<ProtectedAppLayout><SellerPublicPage /></ProtectedAppLayout>} />
              <Route path="/my-orders" element={<ProtectedAppLayout><MyOrdersPage /></ProtectedAppLayout>} />
              <Route path="/my-orders/:id" element={<ProtectedAppLayout><MyOrderDetailPage /></ProtectedAppLayout>} />

              {/* Seller console */}
              <Route path="/seller/apply" element={<ProtectedRoute><AppLayout><SellerApplyPage /></AppLayout></ProtectedRoute>} />
              <Route path="/seller" element={<ProtectedSellerLayout><SellerDashboardPage /></ProtectedSellerLayout>} />
              <Route path="/seller/products" element={<ProtectedSellerLayout><SellerProductsPage /></ProtectedSellerLayout>} />
              <Route path="/seller/orders" element={<ProtectedSellerLayout><SellerOrdersPage /></ProtectedSellerLayout>} />
              <Route path="/seller/orders/:id" element={<ProtectedSellerLayout><SellerOrdersPage /></ProtectedSellerLayout>} />
              <Route path="/seller/storefront" element={<ProtectedSellerLayout><SellerStorefrontPage /></ProtectedSellerLayout>} />

              {/* Admin */}
              <Route path="/admin"           element={<ProtectedAdminLayout><AdminOverviewPage /></ProtectedAdminLayout>} />
              <Route path="/admin/scrapes"   element={<ProtectedAdminLayout><AdminScrapesPage /></ProtectedAdminLayout>} />
              <Route path="/admin/catalogue" element={<ProtectedAdminLayout><AdminCataloguePage /></ProtectedAdminLayout>} />
              <Route path="/admin/products"  element={<ProtectedAdminLayout><AdminProductsPage /></ProtectedAdminLayout>} />
              <Route path="/admin/orders"    element={<ProtectedAdminLayout><AdminOrdersPage /></ProtectedAdminLayout>} />
              <Route path="/admin/orders/:id" element={<ProtectedAdminLayout><AdminOrderDetailPage /></ProtectedAdminLayout>} />
              <Route path="/admin/sellers"   element={<ProtectedAdminLayout><AdminSellersPage /></ProtectedAdminLayout>} />
              <Route path="/admin/suppliers/applications" element={<ProtectedAdminLayout><AdminSupplierApplicationsPage /></ProtectedAdminLayout>} />
              <Route path="/admin/suppliers/applications/:id" element={<ProtectedAdminLayout><AdminSupplierApplicationDetailPage /></ProtectedAdminLayout>} />
              <Route path="/admin/foodpreneurs/approvals" element={<ProtectedAdminLayout><AdminFoodpreneurApprovalsPage /></ProtectedAdminLayout>} />
              <Route path="/admin/approvals" element={<ProtectedAdminLayout><AdminApprovalsHubPage /></ProtectedAdminLayout>} />

              <Route path="/admin/users"     element={<ProtectedAdminLayout><AdminUsersPage /></ProtectedAdminLayout>} />
              <Route path="/admin/notifications" element={<ProtectedAdminLayout><NotificationsPage /></ProtectedAdminLayout>} />
              <Route path="/admin/data/requests"   element={<ProtectedAdminLayout><ProductRequestsPage /></ProtectedAdminLayout>} />
              <Route path="/admin/data/products"   element={<ProtectedAdminLayout><ProductManagementPage /></ProtectedAdminLayout>} />
              <Route path="/admin/data/stores"     element={<ProtectedAdminLayout><StoreManagementPage /></ProtectedAdminLayout>} />
              <Route path="/admin/data/categories" element={<ProtectedAdminLayout><CategoryManagementPage /></ProtectedAdminLayout>} />
              <Route path="/admin/data/scrapes"    element={<ProtectedAdminLayout><ScrapeManagementPage /></ProtectedAdminLayout>} />

              {/* Internal staff dashboards */}
              <Route path="/staff/rep" element={<StaffRoute allow={['rep']}><AppLayout><RepDashboardPage /></AppLayout></StaffRoute>} />
              <Route path="/staff/capturer" element={<StaffRoute allow={['capturer']}><AppLayout><CapturerDashboardPage /></AppLayout></StaffRoute>} />
              <Route path="/staff/manager" element={<StaffRoute allow={['manager']}><AppLayout><ManagerDashboardPage /></AppLayout></StaffRoute>} />
              <Route path="/staff/manager/users" element={<StaffRoute allow={['manager']}><AppLayout><ManagerUsersPage /></AppLayout></StaffRoute>} />
              <Route path="/staff/manager/suppliers/new" element={<StaffRoute allow={['manager']}><AppLayout><ManagerCreateSupplierPage /></AppLayout></StaffRoute>} />
              <Route path="/staff/manager/supplier-changes" element={<StaffRoute allow={['manager']}><AppLayout><ManagerSupplierChangesPage /></AppLayout></StaffRoute>} />

              {/* Supplier portal */}
              <Route path="/supplier" element={<StaffRoute allow={['supplier']}><AppLayout><SupplierHomePage /></AppLayout></StaffRoute>} />
              <Route path="/supplier/profile" element={<StaffRoute allow={['supplier']}><AppLayout><SupplierProfileEditPage /></AppLayout></StaffRoute>} />
              <Route path="/supplier/products/new" element={<StaffRoute allow={['supplier']}><AppLayout><SupplierProductNewPage /></AppLayout></StaffRoute>} />
              <Route path="/supplier/products/:id/edit-price" element={<StaffRoute allow={['supplier']}><AppLayout><SupplierPriceEditPage /></AppLayout></StaffRoute>} />

              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            <MasterCartUI />
          </BrowserRouter>
          </MasterCartProvider>
        </CartProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
